import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { fetchGetMedia } from 'src/services/apollo/media'
import { pickMedia, takePhoto } from 'src/utils/jsBridge/implementation'
import { uploadMedia } from 'src/utils/upload'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { isAndroidWebview } from 'src/utils/userAgent'

export const DEFAULT_ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
]

type UseMediaUploadOptions = {
  acceptedMimeTypes?: string[]
  multiple?: boolean
  /** Called when a mediaId is obtained (from file upload or native bridge). */
  onMediaId?: (mediaId: string) => void
  /**
   * If provided, the file picker yields raw `File` objects instead of uploading.
   * Useful for consumers that need to preprocess files before uploading.
   */
  onFiles?: (files: File[]) => void
}

/**
 * Shared hook for platform-aware media upload.
 *
 * Encapsulates:
 * - Hidden file input management (ref, props, trigger)
 * - Native gallery / camera via JS bridge (Android webview)
 * - File upload with progress tracking
 * - Platform detection for available upload sources
 */
export function useMediaUpload(options: UseMediaUploadOptions) {
  const { t } = useTranslation(['common'])
  const { onMediaId, onFiles, multiple } = options
  const acceptedMimeTypes =
    options.acceptedMimeTypes ?? DEFAULT_ACCEPTED_MIME_TYPES

  const isAndroidWebView = isAndroidWebview()

  // ── File input ──────────────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement>(null)

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // ── Upload ──────────────────────────────────────────────────

  const [uploadProgress, setUploadProgress] = useState(0)

  const [uploading, performUpload] = useLoadingCallback(
    async (...files: File[]) => {
      for (let i = 0; i < files.length; i++) {
        try {
          setUploadProgress(i / files.length)
          const result = await uploadMedia(files[i], {
            onProgress: p => setUploadProgress((i + p) / files.length),
          })
          const mediaId = result.media?.id
          if (mediaId) {
            onMediaId?.(mediaId)
          }
        } catch (e) {
          console.error(e)
          toast.error(t('common:error:failed-to-upload'))
        }
      }
    },
    [onMediaId, t],
  )

  // ── File input change ───────────────────────────────────────

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files?.length) return
      e.target.value = ''

      if (onFiles) {
        onFiles([...files])
      } else {
        performUpload(...files)
      }
    },
    [onFiles, performUpload],
  )

  // ── Native handlers ─────────────────────────────────────────

  const openNativeGallery = useCallback(async () => {
    try {
      const data = await pickMedia({ mimeTypes: acceptedMimeTypes })
      if (!data?.mediaId) return
      // Validate media was created & warm the Apollo cache
      const res = await fetchGetMedia({ id: data.mediaId })
      if (!res.data?.media) return
      onMediaId?.(data.mediaId)
    } catch (e) {
      console.error(e)
      toast.error(t('common:error:failed-to-upload'))
    }
  }, [acceptedMimeTypes, onMediaId, t])

  const openNativeCamera = useCallback(async () => {
    try {
      const data = await takePhoto()
      if (!data?.mediaId) return
      const res = await fetchGetMedia({ id: data.mediaId })
      if (!res.data?.media) return
      onMediaId?.(data.mediaId)
    } catch (e) {
      console.error(e)
      toast.error(t('common:error:failed-to-upload'))
    }
  }, [onMediaId, t])

  // ── File input props (for rendering the hidden <input>) ─────

  const fileInputProps = {
    ref: fileInputRef,
    type: 'file' as const,
    accept: acceptedMimeTypes.join(','),
    multiple: multiple ?? false,
    onChange: handleFileInputChange,
    className: 'hidden' as const,
  }

  return {
    /** Props to spread on a hidden `<input>` element. */
    fileInputProps,
    /** Programmatically open the OS file picker. */
    openFilePicker,
    /** Open the native photo gallery (Android webview). */
    openNativeGallery,
    /** Open the native camera (Android webview). */
    openNativeCamera,
    /** Whether a file is currently being uploaded. */
    uploading,
    /** Upload progress from 0 to 1. */
    uploadProgress,
    /** Trigger an upload programmatically. */
    performUpload,
    /** Whether multiple upload sources exist (gallery + camera on Android webview). */
    hasMultipleUploadSources: isAndroidWebView,
    /** Resolved MIME types. */
    acceptedMimeTypes,
  }
}
